import { useMutation } from "@tanstack/react-query";

import {
  ConnectApiVersions,
  connectApiPost,
} from "src/api/connectApi/connectApi";
import { manifestBuilderApi } from "src/api/endpoints";
import {
  DocumentationEntry,
  OnePasswordLinkEntry,
  PromoteFlowVersionRequest,
} from "src/clients/flow-api";
import { ToastParams } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { ProviderType } from "src/flowContainer/ManifestPromotion/types";
import { errorMessage } from "src/utils/stringUtils";

export type JinjaFile = {
  content: string;
  name: string;
};

const showSupportErrorToast = (title: string, error: Error) => {
  toastActions.failure({
    title,
    description: errorMessage(error),
    actionText: "Contact Support",
    onActionClick: () => {
      window.open(
        "mailto:support@taktile.com",
        "_blank",
        "noopener noreferrer",
      );
    },
  });
};

type CompileManifestResult = {
  manifest: Record<string, unknown>;
  jinja_file: JinjaFile | null;
  test_case_rows: Record<string, unknown>[] | null;
  sample_response_rows: Record<string, unknown>[] | null;
  flow_url: string;
};

type PromoteFlowVersionParams = {
  flowVersionId: string;
  providerType: ProviderType;
  existingProviderName?: string;
  newProviderDescription?: string;
  onePasswordLinks?: OnePasswordLinkEntry[];
  documentations?: DocumentationEntry[];
  errorHandling?: string;
  comments?: string;
  userEmail: string;
};

export const usePromoteFlowVersionToManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({
      flowVersionId,
      providerType,
      existingProviderName,
      newProviderDescription,
      onePasswordLinks,
      documentations,
      errorHandling,
      comments,
      userEmail,
    }: PromoteFlowVersionParams) => {
      const toastId = toastActions.loading({
        title: "Promoting flow version to manifest",
        description:
          "Please wait while we promote the flow version to a manifest",
      });
      try {
        const requestData: PromoteFlowVersionRequest = {
          flow_version_id: flowVersionId,
          provider_type: providerType,
          existing_provider_name: existingProviderName,
          new_provider_description: newProviderDescription,
          one_password_links: onePasswordLinks ?? [],
          documentations: documentations ?? [],
          error_handling: errorHandling,
          comments,
          user_email: userEmail,
        };
        const response =
          await manifestBuilderApi.promoteFlowVersionToManifestApiV2ManifestBuilderPromotePost(
            requestData,
          );
        return response.data;
      } finally {
        toastActions.dismiss(toastId);
      }
    },
    onSuccess: (data) => {
      const params: ToastParams = {
        title: "Flow promoted successfully",
        description: `Provider: ${data.provider_name} | Branch: ${data.branch_name}`,
      };
      toastActions.success(params);
    },
    onError: (error: Error) => {
      showSupportErrorToast("Failed to promote flow version", error);
    },
  });
};

type UploadManifestParams = {
  flowVersionId: string;
};

export const useUploadManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({ flowVersionId }: UploadManifestParams) => {
      const toastId = toastActions.loading({
        title: "Uploading manifest",
        description: "Please wait while we upload the manifest",
      });
      try {
        const response =
          await manifestBuilderApi.uploadManifestApiV2ManifestBuilderUploadPost(
            {
              flow_version_id: flowVersionId,
            },
          );
        return response.data;
      } finally {
        toastActions.dismiss(toastId);
      }
    },
    onSuccess: () => {
      const params: ToastParams = {
        title: "Manifest uploaded successfully",
      };
      toastActions.success(params);
    },
    onError: (error: Error) => {
      showSupportErrorToast("Failed to upload manifest", error);
    },
  });
};

type CompileManifestParams = {
  flowVersionId: string;
};

export const useCompileManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({
      flowVersionId,
    }: CompileManifestParams): Promise<CompileManifestResult> => {
      const toastId = toastActions.loading({
        duration: null,
        title: "Compiling manifest",
        description: "Please wait while we compile the manifest",
      });
      try {
        const response =
          await manifestBuilderApi.compileManifestEndpointApiV2ManifestBuilderCompilePost(
            { flow_version_id: flowVersionId },
          );
        const {
          manifest,
          jinja_file,
          test_case_rows,
          sample_response_rows,
          flow_url,
        } = response.data;
        return {
          manifest: manifest as Record<string, unknown>,
          jinja_file: jinja_file ?? null,
          test_case_rows:
            (test_case_rows as CompileManifestResult["test_case_rows"]) ?? null,
          sample_response_rows:
            (sample_response_rows as CompileManifestResult["sample_response_rows"]) ??
            null,
          flow_url,
        };
      } finally {
        toastActions.dismiss(toastId);
      }
    },
    onError: (error: Error) => {
      showSupportErrorToast("Failed to compile manifest", error);
    },
  });
};

type UploadCompiledManifestParams = {
  baseUrl: string;
  manifest: Record<string, unknown>;
  flowUrl: string;
  sampleResponseRows: Record<string, unknown>[] | null;
  jinjaFile: JinjaFile | null;
};

export const useUploadCompiledManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({
      baseUrl,
      manifest,
      flowUrl,
      sampleResponseRows,
      jinjaFile,
    }: UploadCompiledManifestParams) => {
      const toastId = toastActions.loading({
        duration: null,
        title: "Uploading compiled manifest",
        description: "Please wait while we upload the manifest",
      });
      try {
        const response = await connectApiPost(
          baseUrl,
          `/manifest/:uploadCompiled`,
          {
            manifest,
            flow_url: flowUrl,
            sample_response_rows: sampleResponseRows,
            jinja_file: jinjaFile,
          },
          { version: ConnectApiVersions.V2 },
        );
        return response.data;
      } finally {
        toastActions.dismiss(toastId);
      }
    },
    onSuccess: () => {
      toastActions.success({
        title: "Compiled manifest uploaded successfully",
      });
    },
    onError: (error: Error) => {
      showSupportErrorToast("Failed to upload compiled manifest", error);
    },
  });
};
